import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { loadBanksWithCurrentBalance } from '../../../core/utils/bank';
import { formatCurrency, formatDateBR } from '../../../core/utils/format';
import { ENTRY_TYPE, PROJECTED_HORIZONS } from '../../../core/constants/financial';
import {
  loadProjectedMovements,
  computeDailyProjection,
  getInitialBalance,
} from '../../../core/utils/projectedCashFlow';
import { useTableSort } from '../../../core/hooks/useTableSort';
import SortableTh from './SortableTh';
import ProjectedCashFlowChart from './ProjectedCashFlowChart';

const SORT_ACCESSORS = {
  data: (r) => r.date || '',
  descricao: (r) => r.description || '',
  banco: (r) => r.bank || '',
  tipo: (r) => r.type || '',
  valor: (r) => r.value,
  saldo: (r) => r.saldoAcumulado,
};

const ProjectedCashFlowModal = ({ isOpen, onClose }) => {
  const company = useSelector((state) => state.app.company || {});
  const [horizonDays, setHorizonDays] = useState(15);
  const [selectedBank, setSelectedBank] = useState('');
  const [banks, setBanks] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('grafico'); // 'grafico' | 'tabela'

  useEffect(() => {
    if (!isOpen) return;
    setHorizonDays(15);
    setSelectedBank('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !company.id) return;
    loadBanksWithCurrentBalance(company.id).then((data) => setBanks(data || []));
  }, [isOpen, company.id]);

  useEffect(() => {
    if (!isOpen || !company.id) return;
    let cancelled = false;
    setLoading(true);
    loadProjectedMovements({
      companyId: company.id,
      bankId: selectedBank || undefined,
      horizonDays,
    }).then((data) => {
      if (cancelled) return;
      setMovements(data || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [isOpen, company.id, selectedBank, horizonDays]);

  const initialBalance = useMemo(() => getInitialBalance(banks, selectedBank), [banks, selectedBank]);
  const projection = useMemo(
    () => computeDailyProjection(movements, initialBalance, horizonDays),
    [movements, initialBalance, horizonDays]
  );

  const totalEntradas = useMemo(
    () => projection.reduce((s, d) => s + d.entradas, 0),
    [projection]
  );
  const totalSaidas = useMemo(
    () => projection.reduce((s, d) => s + d.saidas, 0),
    [projection]
  );
  const saldoFinal = projection.length ? projection[projection.length - 1].saldoAcumulado : initialBalance;
  const diasNegativos = useMemo(
    () => projection.filter((d) => d.saldoAcumulado < 0).length,
    [projection]
  );

  const rows = useMemo(() => {
    let acc = initialBalance;
    return movements.map((m) => {
      const value = Number(m.value) || 0;
      const isReceita = m.financial_entry?.type === ENTRY_TYPE.RECEITA;
      acc += isReceita ? value : -value;
      return {
        id: m.id,
        date: m.due_date,
        description: m.financial_entry?.description || m.financial_entry?.supplier_client || '—',
        bank: m.Bank?.name || '—',
        type: isReceita ? 'Entrada' : 'Saída',
        value: isReceita ? value : -value,
        saldoAcumulado: acc,
      };
    });
  }, [movements, initialBalance]);

  const { sorted, sort, toggleSort } = useTableSort(rows, SORT_ACCESSORS);

  if (!isOpen) return null;

  return (
    <div className="fd-modal-overlay" onClick={onClose}>
      <div className="fd-modal fd-modal--lg fd-projected" onClick={(e) => e.stopPropagation()}>
        <div className="fd-modal-header">
          <div className="fd-modal-header-left">
            <h3 className="fd-modal-title" style={{ color: '#60b4f6' }}>FLUXO DE CAIXA PROJETADO</h3>
            <span className="fd-table-muted" style={{ fontSize: 10 }}>Entradas e saídas previstas com saldo acumulado</span>
          </div>
          <div className="fd-modal-header-right">
            {banks.length > 0 && (
              <select
                className="fd-installment-input"
                value={selectedBank}
                onChange={(e) => setSelectedBank(e.target.value)}
                style={{ minWidth: 140 }}
              >
                <option value="">Todos os bancos</option>
                {banks.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
            <button className="fd-btn-close" type="button" onClick={onClose}>Fechar</button>
          </div>
        </div>

        <div className="fd-period-filters">
          <span className="fd-period-filters-label">Horizonte:</span>
          {PROJECTED_HORIZONS.map((h) => (
            <button
              key={h.days}
              type="button"
              className={`fd-period-btn ${horizonDays === h.days ? 'fd-period-btn--active' : ''}`}
              onClick={() => setHorizonDays(h.days)}
            >
              {h.label}
            </button>
          ))}
          <span className="fd-projected-balance-display">
            Saldo inicial: <strong>{formatCurrency(initialBalance)}</strong>
          </span>
        </div>

        <div className="fd-projected-kpis">
          <div className="fd-projected-kpi"><span style={{ color: '#70E000' }}>Entradas Previstas</span><strong style={{ color: '#70E000' }}>{formatCurrency(totalEntradas)}</strong></div>
          <div className="fd-projected-kpi"><span style={{ color: '#e88c2a' }}>Saídas Previstas</span><strong style={{ color: '#e88c2a' }}>{formatCurrency(totalSaidas)}</strong></div>
          <div className="fd-projected-kpi"><span style={{ color: '#60b4f6' }}>Saldo Projetado</span><strong style={{ color: saldoFinal >= 0 ? '#60b4f6' : '#ED2D2D' }}>{formatCurrency(saldoFinal)}</strong></div>
          <div className="fd-projected-kpi"><span>Dias com Saldo Negativo</span><strong>{diasNegativos}</strong></div>
        </div>

        <div className="fd-projected-toggle-row">
          <div className="fd-projected-view-toggle">
            <button type="button" className={view === 'grafico' ? 'active' : ''} onClick={() => setView('grafico')}>Gráfico</button>
            <button type="button" className={view === 'tabela' ? 'active' : ''} onClick={() => setView('tabela')}>Tabela</button>
          </div>
        </div>

        {loading ? (
          <div className="fd-table-empty" style={{ padding: 40 }}>Carregando...</div>
        ) : view === 'grafico' ? (
          <div className="fd-projected-chart-wrapper">
            <ProjectedCashFlowChart data={projection} />
          </div>
        ) : (
          <div className="fd-modal-table-wrapper">
            <table className="fd-modal-table">
              <thead>
                <tr>
                  <SortableTh label="DATA" sortKey="data" sort={sort} onSort={toggleSort} style={{ width: 100 }} />
                  <SortableTh label="DESCRIÇÃO" sortKey="descricao" sort={sort} onSort={toggleSort} />
                  <SortableTh label="BANCO" sortKey="banco" sort={sort} onSort={toggleSort} style={{ width: 120 }} />
                  <SortableTh label="TIPO" sortKey="tipo" sort={sort} onSort={toggleSort} style={{ width: 80 }} />
                  <SortableTh label="VALOR" sortKey="valor" sort={sort} onSort={toggleSort} style={{ width: 120, textAlign: 'right' }} />
                  <SortableTh label="SALDO ACUM." sortKey="saldo" sort={sort} onSort={toggleSort} style={{ width: 130, textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={6} className="fd-table-empty">Sem movimentações pendentes no período</td></tr>
                ) : sorted.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDateBR(r.date)}</td>
                    <td>{r.description}</td>
                    <td>{r.bank}</td>
                    <td><span style={{ color: r.type === 'Entrada' ? '#70E000' : '#e88c2a' }}>{r.type}</span></td>
                    <td style={{ textAlign: 'right', fontFamily: "'Rajdhani', sans-serif", color: r.value >= 0 ? '#70E000' : '#e88c2a' }}>
                      {formatCurrency(r.value)}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: "'Rajdhani', sans-serif", color: r.saldoAcumulado >= 0 ? '#60b4f6' : '#ED2D2D' }}>
                      {formatCurrency(r.saldoAcumulado)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectedCashFlowModal;
